import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionLabel, StatBlock } from "@/components/Primitives";
import { Loader2, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";

function fmt(n) { return (n ?? 0).toLocaleString("en-US"); }

export default function Reports() {
  const [items, setItems] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [advFilter, setAdvFilter] = useState("all");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const [{ data: r }, { data: s }, { data: a }] = await Promise.all([
      api.get("/campaigns/reports/latest"),
      api.get("/stats/overview"),
      api.get("/advertisers/"),
    ]);
    setItems(r);
    setStats(s);
    setAdvertisers(a);
  };

  useEffect(() => {
    (async () => { try { await load(); } finally { setLoading(false); } })();
  }, []);

  const filtered = useMemo(() => {
    if (advFilter === "all") return items;
    return items.filter((it) => it.campaign.advertiser_id === advFilter);
  }, [items, advFilter]);

  const syncAll = async () => {
    setSyncing(true);
    let ok = 0; let fail = 0;
    for (const it of filtered) {
      if (it.campaign.push_status !== "accepted") continue;
      try { await api.post(`/campaigns/${it.campaign.id}/sync`); ok++; }
      catch { fail++; }
    }
    await load();
    setSyncing(false);
    toast.success(`Synced ${ok} campaign(s)${fail ? `, ${fail} failed` : ""}`);
  };

  const totals = filtered.reduce((acc, it) => {
    const m = it.latest_report?.metrics;
    if (!m) return acc;
    acc.impressions += m.impressions || 0;
    acc.clicks += m.clicks || 0;
    acc.spend += m.spend_usd || 0;
    acc.conversions += m.conversions || 0;
    return acc;
  }, { impressions: 0, clicks: 0, spend: 0, conversions: 0 });

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1500px]">
      <PageHeader
        eyebrow="Module / 06"
        title="Reports Dashboard"
        description="Delivery & performance across pushed campaigns, pulled from the reporting platform. Background sync every 5 minutes; manual sync available below."
        testid="reports-header"
        actions={
          <button onClick={syncAll} disabled={syncing || filtered.length === 0}
            data-testid="reports-sync-all-button"
            className="bg-[#FF3E00] text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#D93500] disabled:opacity-60">
            {syncing ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
            {syncing ? "Syncing" : "Sync all"}
          </button>
        }
      />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <select
          data-testid="reports-advertiser-filter"
          value={advFilter}
          onChange={(e) => setAdvFilter(e.target.value)}
          className="bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
        >
          <option value="all">All advertisers</option>
          {advertisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <p className="text-xs font-mono uppercase tracking-[0.15em] text-[#555] self-center ml-auto">
          {filtered.length} of {items.length} campaigns
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatBlock label="Impressions" value={fmt(totals.impressions)} testid="agg-impressions"/>
        <StatBlock label="Clicks" value={fmt(totals.clicks)} testid="agg-clicks"/>
        <StatBlock label="Conversions" value={fmt(totals.conversions)} testid="agg-conversions"/>
        <StatBlock label="Spend" value={`$${totals.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} sublabel={stats ? `${stats.accepted_campaigns} accepted / ${stats.total_campaigns} total` : ""} testid="agg-spend"/>
      </div>

      <SectionLabel>Campaign performance</SectionLabel>
      {loading ? (
        <p className="text-sm text-[#555] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#E0E0D8] p-12 text-center">
          <p className="text-sm text-[#555]">
            {items.length === 0 ? "No campaigns to report on yet." : "No campaigns for this advertiser."}
          </p>
          {items.length === 0 && (
            <Link to="/campaigns/new" className="inline-block mt-4 bg-[#FF3E00] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D93500]">
              Push a campaign
            </Link>
          )}
        </div>
      ) : (
        <div className="border border-[#E0E0D8] bg-white overflow-x-auto" data-testid="reports-table">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A]">
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Campaign</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Advertiser</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Impr.</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">CTR</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Conv.</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Spend</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Delivery</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Last synced</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ campaign, latest_report }) => {
                const m = latest_report?.metrics;
                return (
                  <tr key={campaign.id} className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0]" data-testid={`report-row-${campaign.id}`}>
                    <td className="py-3 px-4 text-sm font-medium">{campaign.name}</td>
                    <td className="py-3 px-4 text-sm">{campaign.advertiser}</td>
                    <td className="py-3 px-4 text-right font-mono">{m ? fmt(m.impressions) : "—"}</td>
                    <td className="py-3 px-4 text-right font-mono">{m ? `${m.ctr}%` : "—"}</td>
                    <td className="py-3 px-4 text-right font-mono">{m ? fmt(m.conversions) : "—"}</td>
                    <td className="py-3 px-4 text-right font-mono">{m ? `$${m.spend_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}</td>
                    <td className="py-3 px-4 text-right font-mono">{m ? `${m.delivery_pct}%` : "—"}</td>
                    <td className="py-3 px-4 font-mono text-[11px] text-[#555]">
                      {campaign.last_report_synced_at ? new Date(campaign.last_report_synced_at).toLocaleString() : "Not synced"}
                    </td>
                    <td className="py-3 px-4">
                      <Link to={`/campaigns/${campaign.id}`} className="text-[11px] font-mono uppercase tracking-wider text-[#FF3E00] inline-flex items-center gap-1 hover:underline">
                        Open <ArrowRight size={11} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
